import React, { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";

import AppModal from "../AppModal";

const dataUrlToFile = async (dataUrl, fileName) => {
	const res = await fetch(dataUrl);
	const blob = await res.blob();
	return new File([blob], fileName, { type: "image/png" });
};

export default function CustomWebcam({ open, onClose, onCapture }) {
	const webcamRef = useRef(null);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [capturedFile, setCapturedFile] = useState(null);
	const [error, setError] = useState(null);
	const [capturing, setCapturing] = useState(false);

	useEffect(() => {
		if (!open) {
			setPreviewUrl(null);
			setCapturedFile(null);
			setError(null);
			setCapturing(false);
		}
	}, [open]);

	const handleCapture = useCallback(async () => {
		const imageSrc = webcamRef.current?.getScreenshot();
		if (!imageSrc) {
			setError("Não foi possível capturar a imagem. Verifique a permissão da câmera.");
			return;
		}

		setCapturing(true);
		setError(null);

		try {
			const fileName = `${Date.now()}.png`;
			const file = await dataUrlToFile(imageSrc, fileName);
			setPreviewUrl(imageSrc);
			setCapturedFile(file);
		} catch (_) {
			setError("Erro ao processar a imagem. Tente novamente.");
		} finally {
			setCapturing(false);
		}
	}, []);

	const handleRetake = () => {
		setPreviewUrl(null);
		setCapturedFile(null);
		setError(null);
	};

	const handleConfirm = () => {
		if (!capturedFile) return;
		onCapture(capturedFile);
	};

	const footer = (
		<div className="flex flex-wrap items-center justify-end gap-2">
			{previewUrl ? (
				<>
					<button type="button" className="btn btn-ghost" onClick={handleRetake}>
						Tirar novamente
					</button>
					<button type="button" className="btn btn-primary" onClick={handleConfirm}>
						Usar foto
					</button>
				</>
			) : (
				<>
					<button type="button" className="btn btn-ghost" onClick={onClose}>
						Cancelar
					</button>
					<button
						type="button"
						className="btn btn-primary"
						onClick={handleCapture}
						disabled={capturing}
					>
						{capturing ? "Capturando..." : "Capturar"}
					</button>
				</>
			)}
		</div>
	);

	return (
		<AppModal
			open={open}
			onClose={onClose}
			title="Câmera"
			subtitle="Posicione a imagem e capture para enviar"
			maxWidth="min(calc(100vw - 1.5rem), 840px)"
			footer={footer}
			bodyClassName="tailwick-camera-modal__body"
		>
			<div className="tailwick-camera-modal__viewport">
				{error && (
					<p className="tailwick-camera-modal__error" role="alert">
						{error}
					</p>
				)}
				{previewUrl ? (
					<img
						src={previewUrl}
						alt="Pré-visualização"
						className="tailwick-camera-modal__preview"
					/>
				) : (
					<Webcam
						ref={webcamRef}
						audio={false}
						screenshotFormat="image/png"
						videoConstraints={{ facingMode: "user", width: 1280, height: 720 }}
						className="tailwick-camera-modal__webcam"
						onUserMediaError={() => {
							setError(
								"Não foi possível acessar a câmera. Verifique as permissões do navegador."
							);
						}}
					/>
				)}
			</div>
		</AppModal>
	);
}
